import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setMapBounds } from 'src/store/actions/mapBounds'

// We can't import Leaflet as usual because it breaks on server-side.
// Also, we can't conditionally import, so we have to fall back to require
declare const window: any
declare const __CLIENT__: boolean
const L = __CLIENT__ ? require('leaflet') : null
__CLIENT__ && require('leaflet.vectorgrid/dist/Leaflet.VectorGrid.bundled.js')

const roadMap =
  'https://api.mapbox.com/styles/v1/dianareinoso/ckrbvekpc0x9817pnmgbdedch/tiles/256/{z}/{x}/{y}@2x' +
  '?access_token=pk.eyJ1IjoiZGlhbmFyZWlub3NvIiwiYSI6ImNpd25nM2xnazAwMTcydHAxOGgycDAwcjAifQ.-XOqK4JIZpbF55dv_xzq2Q'
const satMap =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

const TILE_SERVER =
  process.env.TILE_SERVER || 'https://tilessecurityhub.securitas.es'

export interface useMapParams {
  id: string
  satellite?: boolean
  ref?: MutableRefObject<any>
}

const useMap = ({ id, satellite, ref }: useMapParams) => {
  const dispatch = useDispatch()
  const myOwnRef = useRef(null)
  const theMap = ref || myOwnRef // Careful: don't call useRef conditionally
  const tiles = useRef(null)

  // Map initialization
  useEffect(() => {
    if (theMap.current) return // initialised already?

    theMap.current = L.map(id, {
      boxZoom: false,
      keyboard: false,
      zoomSnap: 1,
      maxZoom: 18,
      minZoom: 5,
      worldCopyJump: true
    })

    const center = [41, -4]
    const zoom = 7
    theMap.current.setView(center, zoom)

    const updateBounds = () => {
      const bounds = theMap.current.getBounds()
      dispatch(
        setMapBounds({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
          zoom: theMap.current.getZoom()
        })
      )
    }
    theMap.current.on('moveend', updateBounds)
    updateBounds()

    if (window.ResizeObserver) {
      const observer = new window.ResizeObserver(() => {
        theMap.current.invalidateSize()
      })
      observer.observe(document.getElementById(id))
    }
  }, [])

  // Set the right tiles
  useEffect(() => {
    if (tiles.current) {
      theMap.current.removeLayer(tiles.current)
    }
    theMap.current.createPane('labels')
    theMap.current.getPane('labels').style.zIndex = 150
    const tileUrl = satellite ? satMap : roadMap
    tiles.current = new L.TileLayer(tileUrl, { pane: 'labels' })
    tiles.current.addTo(theMap.current)
  }, [satellite])

  return theMap
}

const smartTile = (t, e, n) =>
  t.z > 12 ? new L.SVG.Tile(t, e, n) : new L.Canvas.Tile(t, e, n)

export const getVectorGrid = (
  id: string,
  styles: Record<string, any>,
  extraOpts?: any
) =>
  L.vectorGrid.protobuf(TILE_SERVER + `/data/${id}/{z}/{x}/{y}.pbf`, {
    rendererFactory: smartTile,
    maxNativeZoom: 13,
    getFeatureId: (f) => {
      if (f.properties.cs) return 'CS/' + f.properties.cs
      if (f.properties.zip) return 'ZIP/' + f.properties.zip
      if (f.properties.mun) return 'MUN/' + f.properties.mun
      if (f.properties.pro) return 'PRO/' + f.properties.pro
      return 'unknown'
    },
    vectorTileLayerStyles: styles,
    ...extraOpts
  })

export const useZoom = (theMap: MutableRefObject<any>): number => {
  const [zoom, setZoom] = useState(
    theMap.current ? theMap.current.getZoom() : 0
  )

  useEffect(() => {
    const handler = () => setZoom(theMap.current.getZoom())
    theMap.current.on('zoomend', handler)
    return () => theMap.current.off('zoomend', handler)
  }, [])

  return zoom
}

export default useMap
